import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, UtensilsCrossed, Coffee, Salad, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { getEffectiveLunchWeekInfo, getEffectiveLunchDayName, isShowingNextMondayLunch } from '@/lib/dateUtils';

const buffetFeatures = [
  { icon: UtensilsCrossed, label: 'Buffé' },
  { icon: Coffee, label: 'Dryck' },
  { icon: Salad, label: 'Salladsbar' },
  { icon: Cookie, label: 'Dessert' },
];

export const TodaysLunchSection = () => {
  const { getMenuForWeek } = useApp();
  
  // Använd effektivt lunchdatum (hanterar helger automatiskt)
  const effectiveWeekInfo = getEffectiveLunchWeekInfo();
  const effectiveDayName = getEffectiveLunchDayName();
  const isNextMonday = isShowingNextMondayLunch();
  
  const menu = getMenuForWeek(effectiveWeekInfo.week, effectiveWeekInfo.year);
  const displayMenu = menu?.items.find(item => item.day === effectiveDayName) || menu?.items[0];

  // Om ingen meny finns för veckan, visa ett tydligt fallback-meddelande
  if (!menu) {
    return (
      <section className="section-padding bg-secondary/30">
        <div className="container-wide mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Veckans lunch
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Vecka {effectiveWeekInfo.week}
            </h2>
            <div className="card-warm p-8 md:p-10">
            <p className="text-muted-foreground text-lg">
                Menyn för vecka {effectiveWeekInfo.week} är inte publicerad ännu.
              </p>
              <p className="text-muted-foreground mt-2">
                Kom tillbaka snart för att se vad som serveras!
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!displayMenu) {
    return null;
  }

  return (
    <section className="section-padding bg-secondary/30">
      <div className="container-wide mx-auto">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              {isNextMonday ? 'Nästa måndags lunch' : 'Dagens lunch'}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
              Vecka {menu.week}
            </h2>
          </div>

          {/* Buffé-info highlight */}
          <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/10">
            <p className="text-center text-sm font-medium text-foreground mb-3">
              Allt ingår i lunchen – serveras som buffé
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {buffetFeatures.map((feature) => (
                <div
                  key={feature.label}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/80 text-sm text-muted-foreground"
                >
                  <feature.icon size={14} className="text-primary" />
                  <span>{feature.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card-warm p-8 md:p-10">
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-primary/5 border border-primary/10">
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-1">
                    {displayMenu.dish}
                  </h3>
                </div>
                
                {displayMenu.dish2 && (
                <div className="p-5 rounded-xl bg-secondary/40 border border-secondary/50">
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-1">
                    {displayMenu.dish2}
                  </h3>
                </div>
                )}

                {displayMenu.comment && (
                  <p className="text-muted-foreground italic px-2">{displayMenu.comment}</p>
                )}
              </div>

              {displayMenu.vegetarian && (  
                <div className="flex items-start gap-3 p-4 rounded-lg bg-vegetarian-light">
                  <Leaf size={20} className="text-vegetarian mt-0.5" />
                  <div>
                    <p className="font-medium text-vegetarian text-sm mb-1">Vegetariskt alternativ</p>
                    <p className="text-foreground">{displayMenu.vegetarian}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border">
                <p className="text-muted-foreground">
                  Lunch serveras <span className="font-medium text-foreground">10:30–13:30</span>
                </p>
                <Button asChild variant="default">
                  <Link to="/veckans-lunch">
                    Se hela veckan
                    <ArrowRight size={16} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
